import { call, put, takeEvery } from "redux-saga/effects";
import requestWithSubTenantIfNeeded from "../requestWithSubTenantIfNeeded";
import { controlledBy } from "@p81-common/shell-core-js";
import * as actions from "./getStartedRefreshActions";
import type { BaseRequestAction } from "types";
import type { WizardDocument } from "types/unifiedWizard";

// Deployed endpoint on the onboarding-domain get-started-lambda.
// NOTE the `-v2` segment — the lambda is fronted at /api/onboarding-v2/*.
const REFRESH_PATH = "onboarding-v2/get-started/refresh";

function hasSections(value: unknown): value is WizardDocument {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { sections?: unknown }).sections)
  );
}

// The lambda response may be the document itself or wrapped as { data }.
function extractWizardDocument(response: unknown): WizardDocument | null {
  if (hasSections(response)) return response;
  const wrapped = (response as { data?: unknown } | null)?.data;
  return hasSections(wrapped) ? wrapped : null;
}

// Quantum-IA detection (SDD Component 9). At document creation the lambda needs
// to know whether the tenant's Internet Access is managed by Quantum — if so the
// IA wizard steps are redundant and the lambda returns `showWizard: false`. We
// read the SWG control status and map `controlledBy` to the flag the lambda
// expects. The lambda evaluates `quantumIa` only once (at creation), so
// re-deriving it on later refreshes is harmless. A failed/absent status call
// defaults to `false` (safe default — never hides the wizard on error).
function* resolveQuantumIa() {
  try {
    const status: unknown = yield call(
      requestWithSubTenantIfNeeded,
      "get",
      "swg/rule/status"
    );
    const value = (status as { data?: { controlledBy?: string } } | null)?.data
      ?.controlledBy;
    return value === controlledBy.QUANTUM;
  } catch {
    return false;
  }
}

function* getStartedRefresh(action: BaseRequestAction) {
  const { resolve, reject } = action;
  try {
    const quantumIa: boolean = yield call(resolveQuantumIa);
    const response: unknown = yield call(
      requestWithSubTenantIfNeeded,
      "put",
      REFRESH_PATH,
      { json: { quantumIa } }
    );
    const data = extractWizardDocument(response);
    if (!data) {
      // The endpoint responded, but not with a WizardDocument (e.g. the lambda
      // stub — P81-87616). A broken contract is treated as a failure: the UI
      // shows a gentle, retryable error rather than fabricated data.
      throw new Error("get-started/refresh returned a malformed document");
    }
    yield put(actions.requestSuccess(data));
    if (resolve) yield call(resolve, data);
  } catch (error) {
    yield put(actions.requestFailure(error as Error));
    if (reject) yield call(reject, error as Error);
  }
}

function* saga() {
  yield takeEvery(actions.GET_STARTED_REFRESH_REQUEST, getStartedRefresh);
}

export default saga;
